import { type FC, useEffect } from 'react';
import { Link, useMatches, useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { ErrorFallback } from '@/components/shared/error-fallback';
import { ROUTES } from '@/constants/routes';
import { useRouteText } from '@/lib/i18n/route-text';
import { reloadForStaleChunk } from '@/lib/stale-chunk';

// The deepest titled match names the failed surface, the same seam `use-document-title` walks.
const surfaceTitle = (
  matches: ReturnType<typeof useMatches>,
): string | undefined => {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const title = matches[i]?.staticData.title;
    if (title) return title;
  }
  return undefined;
};

// The error is not rendered; `defaultOnCatch` ships it to Sentry instead.
export const RouteErrorFallback: FC<ErrorComponentProps> = ({ error }) => {
  const matches = useMatches();
  const router = useRouter();
  const { t } = useTranslation('nav');
  const routeText = useRouteText();
  // Here, not in the loader: a hover preload runs loaders too. Retry stays in case a leave prompt cancels it.
  useEffect(() => {
    reloadForStaleChunk(error);
  }, [error]);
  const title = surfaceTitle(matches);
  // The fallback replaces the page, and with it the page's own gutter; same frame as `RouteNotFound`.
  return (
    <main className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md">
        <ErrorFallback
          title={t('route_error.title', {
            surface: title ? routeText(title) : t('route_error.this_page'),
          })}
          description={t('route_error.description')}
          onRetry={() => void router.invalidate()}
          action={
            <Link
              to={ROUTES.DASHBOARD}
              className="text-fs-13 text-fg-3 hover:text-fg-1 underline"
            >
              {t('route_error.go_to_dashboard')}
            </Link>
          }
        />
      </div>
    </main>
  );
};
